import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AccountLayout from '@/components/feature/AccountLayout';
import SettlementDetailModal from '@/pages/revenue/components/SettlementDetailModal';
import DailyRevenueTab from '@/pages/revenue/modules/DailyRevenueTab';
import { formatDate, formatMoney } from '@/pages/projects/modules/utils';
import { getRevenueStatusInfo } from '@/utils/revenue';
import {
  fetchSettlementRequests,
  fetchConfirmSettlement,
} from '@/service/api/revenue';

type ViewTab = 'settlement' | 'daily' | 'projects';

interface ProjectRevenueItem {
  projectId: string;
  projectTitle: string;
  totalRevenue: string;
  pendingConfirmation: string;
  lastSettlement: string;
  episodes: number;
  status: 'active' | 'completed' | string;
  channels: string[];
}

/** 获取付款方式文案 (对照 payment_method 枚举说明) */
const getPaymentMethodText = (method?: string) => {
  if (method === 'BANK_TRANSFER') return '银行转账';
  return method || '银行转账';
};

export default function RevenuePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as ViewTab;
  const validTabs: ViewTab[] = ['settlement', 'daily', 'projects'];
  const initialTab: ViewTab = validTabs.includes(tabParam) ? tabParam : 'settlement';

  const [activeTab, setActiveTab] = useState<ViewTab>(initialTab);
  const [periodFilter, setPeriodFilter] = useState('全部月份');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedSettlementId, setSelectedSettlementId] = useState<string | null>(null);
  const [confirmingSettlementId, setConfirmingSettlementId] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(true);

  // 监听 URL 中的 tab 参数变化
  useEffect(() => {
    const t = searchParams.get('tab') as ViewTab;
    if (t && validTabs.includes(t)) {
      setActiveTab(t);
    }
  }, [searchParams]);

  const handleTabChange = (tab: ViewTab) => {
    setActiveTab(tab);
    setSearchParams({ tab }, { replace: true });
    setStatusFilter('all');
    setPeriodFilter('全部月份');
  };

  // 结算数据
  const [rawSettlements, setRawSettlements] = useState<(Api.Revenue.SettlementResponse | Api.Revenue.SettlementDetailResponse)[]>([]);
  const [projectsRevenueList, setProjectsRevenueList] = useState<ProjectRevenueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  // 从后端获取统一的结算/收益账单数据
  const loadRevenueData = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage('');
    try {
      const settlementsRes = await fetchSettlementRequests();
      const rawItems = Array.isArray(settlementsRes)
        ? settlementsRes
        : (settlementsRes as Api.Revenue.SettlementPageResponse)?.items || [];

      setRawSettlements(rawItems);
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : '获取收益结算数据失败');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRevenueData();
  }, [loadRevenueData]);

  // 计算待确认项
  const pendingSettlements = useMemo(() => {
    return rawSettlements.filter(
      (s) =>
        s.status === 'PENDING_OWNER_CONFIRMATION' ||
        s.status === 'PENDING_PROFILE' ||
        s.status === 'PENDING'
    );
  }, [rawSettlements]);

  const pendingCount = pendingSettlements.length;

  const totalPendingRevenueText = useMemo(() => {
    if (pendingSettlements.length === 0) return '¥ 0.00';
    const first = pendingSettlements[0];
    return formatMoney(first.apply_amount, first.currency, true);
  }, [pendingSettlements]);

  // 动态生成可筛选月份
  const availablePeriods = useMemo(() => {
    const periodSet = new Set<string>();
    rawSettlements.forEach((s) => {
      if (s.period_start_date) {
        periodSet.add(s.period_start_date.substring(0, 7));
      }
    });
    return ['全部月份', ...Array.from(periodSet)];
  }, [rawSettlements]);

  // 按周期与状态筛选结算单
  const filteredSettlements = useMemo(() => {
    return rawSettlements.filter((s) => {
      // 周期筛选
      if (periodFilter !== '全部月份') {
        const periodStr = s.period_start_date ? s.period_start_date.substring(0, 7) : '';
        if (periodStr !== periodFilter) return false;
      }
      // 状态筛选
      if (statusFilter !== 'all') {
        if (statusFilter === 'pending') {
          return (
            s.status === 'PENDING_OWNER_CONFIRMATION' ||
            s.status === 'PENDING_PROFILE' ||
            s.status === 'PENDING'
          );
        }
        if (statusFilter === 'reviewing') {
          return s.status === 'PENDING_REVIEW';
        }
        if (statusFilter === 'paying') {
          return s.status === 'PENDING_PAYMENT' || s.status === 'PAYING';
        }
        if (statusFilter === 'completed') {
          return s.status === 'PAID' || s.status === 'COMPLETED' || s.status === 'SETTLED';
        }
      }
      return true;
    });
  }, [rawSettlements, periodFilter, statusFilter]);

  // 确认结算单处理
  const handleConfirmSettlementOrder = async (settlementId?: string) => {
    if (!settlementId) return;
    if (!confirm('确认完成该笔结算单的核对与确认？确认后将提交结算申请（需结算资料审核通过）。')) return;

    setConfirmingSettlementId(settlementId);
    try {
      await fetchConfirmSettlement(settlementId);
      alert('结算单确认成功！');
      loadRevenueData();
    } catch (err) {
      alert('确认结算单失败: ' + (err instanceof Error ? err.message : '网络错误'));
    } finally {
      setConfirmingSettlementId(null);
    }
  };

  return (
    <AccountLayout
      activePath="/revenue"
      breadcrumbs={[{ label: '账号中心', href: '/profile' }, { label: '收益管理' }]}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-xl text-foreground-950">收益管理</h1>
          <p className="text-xs text-foreground-500 mt-1">查看各项目收益明细、确认月度结算账单、跟踪打款进度</p>
        </div>
        <Link
          to="/projects"
          className="px-4 py-2 rounded-lg border border-primary-300/60 text-primary-700 text-sm font-medium hover:bg-primary-50 transition-all cursor-pointer whitespace-nowrap"
        >
          查看发行项目
        </Link>
      </div>

      {/* Pending Confirmation Banner */}
      {showBanner && pendingCount > 0 && (
        <div className="mb-6 p-5 rounded-2xl bg-accent-50 border border-accent-200/60 relative animate-[fadeIn_0.3s_ease-out]">
          <button
            onClick={() => setShowBanner(false)}
            className="absolute top-3 right-3 w-6 h-6 rounded-full hover:bg-accent-200/50 flex items-center justify-center cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-sm text-foreground-500"></i>
          </button>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-notification-3-line text-accent-600 text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-semibold text-accent-800">
                您有 {pendingCount} 笔收益结算单待确认
              </p>
              <p className="text-xs text-accent-600 mt-1">
                有 {pendingCount} 笔结算单待您确认，合计金额 <strong>{totalPendingRevenueText}</strong>。请及时核对确认，以免影响打款到账。
              </p>
              <button
                onClick={() => {
                  handleTabChange('settlement');
                  setStatusFilter('pending');
                  setPeriodFilter('全部月份');
                }}
                className="mt-3 px-4 py-2 rounded-lg bg-accent-500 text-foreground-950 text-xs font-semibold hover:bg-accent-400 transition-all cursor-pointer whitespace-nowrap"
              >
                立即处理
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-200 text-xs text-red-600 flex items-center justify-between">
          <span>{errorMessage}</span>
          <button onClick={loadRevenueData} className="underline hover:text-red-700 cursor-pointer">重新加载</button>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex items-center justify-between mb-6 border-b border-background-200/70 pb-1 flex-wrap gap-3">
        <div className="flex items-center gap-1">
          {[
            { key: 'settlement' as ViewTab, label: '结算记录', icon: 'ri-bank-card-line' },
            { key: 'daily' as ViewTab, label: '每日收益明细', icon: 'ri-calendar-event-line' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 rounded-t-lg text-sm font-medium whitespace-nowrap transition-all cursor-pointer border-b-2 -mb-[3px] ${activeTab === tab.key
                ? 'text-primary-600 border-primary-500'
                : 'text-foreground-500 border-transparent hover:text-foreground-700 hover:bg-gray-50/50'
                }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              {tab.label}
              {pendingCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-foreground-950 text-xs font-bold">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {/* Filters */}
        {activeTab === 'settlement' && (
          <div className="flex items-center gap-3 text-xs flex-wrap">
            <button
              onClick={loadRevenueData}
              disabled={isLoading}
              className="px-3 py-1.5 rounded-xl border border-gray-200 hover:bg-gray-50 text-foreground-700 flex items-center gap-1.5 transition-colors cursor-pointer disabled:opacity-50"
            >
              <i className={`ri-refresh-line ${isLoading ? 'animate-spin' : ''}`}></i>
              刷新
            </button>
          </div>
        )}
      </div>

      {/* Loading indicator */}
      {isLoading ? (
        <div className="text-center py-20">
          <div className="inline-block w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-sm text-foreground-500">结算数据加载中...</p>
        </div>
      ) : (
        <>
          {/* Unified Settlement & Revenue List Content */}
          {activeTab === 'settlement' && (
            <div>
              {filteredSettlements.length === 0 ? (
                <div className="py-16 text-center text-foreground-400 text-sm glass-card rounded-xl">
                  <i className="ri-bank-card-line text-3xl block mb-2 text-foreground-300"></i>
                  暂无匹配的收益结算记录
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredSettlements.map((s, idx) => {
                    const periodText = s.period_start_date
                      ? `${formatDate(s.period_start_date)} ~ ${formatDate(s.period_end_date)}`
                      : s.settlement_no || '月度结算单';
                    const statusInfo = getRevenueStatusInfo(s.status);
                    const canConfirm =
                      s.status === 'PENDING_OWNER_CONFIRMATION' ||
                      s.status === 'PENDING_PROFILE' ||
                      s.status === 'PENDING';

                    const payment = s.payment;
                    const hasPaymentDetails = Boolean(
                      payment?.payment_method ||
                      payment?.bank_transaction_no ||
                      payment?.payment_no ||
                      payment?.payment_amount ||
                      payment?.remark
                    );

                    return (
                      <div key={s.id || idx} className="p-5 rounded-xl bg-gray-50 border border-gray-200/60 transition-all hover:border-gray-300/80">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center shrink-0 mt-0.5">
                              <i className="ri-bank-card-line text-primary-600 text-lg"></i>
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="text-sm font-semibold text-foreground-950">{periodText}</p>
                                {s.statement_id && (
                                  <span className="px-2 py-0.5 text-[11px] rounded bg-blue-50 text-blue-700 border border-blue-100 font-medium">
                                    账单ID: {s.statement_id}
                                  </span>
                                )}
                                {s.settlement_profile_id && (
                                  <span className="px-2 py-0.5 text-[11px] rounded bg-purple-50 text-purple-700 border border-purple-100 font-medium">
                                    结算资料已关联
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-foreground-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span>结算单号：{s.settlement_no || s.id}</span>
                                <span>申请于 {formatDate(s.applied_at || s.created_at)}</span>
                                {s.owner_confirmed_at && (
                                  <span className="text-emerald-700">确认于 {formatDate(s.owner_confirmed_at)}</span>
                                )}
                                {s.reviewed_at && (
                                  <span className="text-blue-700">审核于 {formatDate(s.reviewed_at)}</span>
                                )}
                                {(payment?.paid_at || s.issued_at) && (
                                  <span className="text-purple-700">到账于 {formatDate(payment?.paid_at || s.issued_at)}</span>
                                )}
                                {s.dispute_submitted_at && (
                                  <span className="text-rose-700">异议于 {formatDate(s.dispute_submitted_at)}</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 self-end sm:self-center">
                            <div className="text-right whitespace-nowrap shrink-0">
                              <span className="text-[11px] text-foreground-400 block whitespace-nowrap">申请结算金额</span>
                              <p className="text-lg font-bold text-foreground-950 whitespace-nowrap">
                                {formatMoney(s.apply_amount, s.currency, true)}
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-1.5 shrink-0">
                              <div className="flex items-center gap-2">
                                <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium whitespace-nowrap shrink-0 ${statusInfo.color}`}>
                                  {statusInfo.label}
                                </span>
                                {canConfirm && (
                                  <button
                                    onClick={() => handleConfirmSettlementOrder(s.id)}
                                    disabled={confirmingSettlementId === s.id}
                                    className="text-xs px-2.5 py-1 rounded bg-primary-500 text-foreground-950 font-medium hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 font-semibold"
                                  >
                                    {confirmingSettlementId === s.id ? '确认中...' : '确认结算'}
                                  </button>
                                )}
                              </div>
                              <button
                                onClick={() => setSelectedSettlementId(s.id || s.settlement_no || null)}
                                className="text-xs px-2.5 py-1 rounded border border-gray-300/80 bg-white hover:bg-gray-50 text-foreground-700 font-medium transition-all cursor-pointer whitespace-nowrap"
                              >
                                查看详情
                              </button>
                            </div>
                          </div>
                        </div>

                        {/* 各种备注说明 (申请说明、异议说明、审核/驳回备注) */}
                        {(s.applicant_note || s.dispute_note || s.audit_remark) && (
                          <div className="mt-3 space-y-2 text-xs">
                            {s.applicant_note && (
                              <div className="p-2.5 rounded-lg bg-white/80 border border-gray-200/50 text-foreground-600">
                                <span className="font-semibold text-foreground-800 mr-1.5">申请说明:</span>
                                {s.applicant_note}
                              </div>
                            )}
                            {s.dispute_note && (
                              <div className="p-2.5 rounded-lg bg-rose-50/80 border border-rose-200/60 text-rose-800">
                                <span className="font-semibold text-rose-900 mr-1.5">异议说明:</span>
                                {s.dispute_note}
                              </div>
                            )}
                            {s.audit_remark && (
                              <div className="p-2.5 rounded-lg bg-amber-50/80 border border-amber-200/60 text-amber-900">
                                <span className="font-semibold text-amber-950 mr-1.5">平台审核备注:</span>
                                {s.audit_remark}
                              </div>
                            )}
                          </div>
                        )}

                        {/* 打款及支付凭证明细 */}
                        {hasPaymentDetails && (
                          <div className="mt-3 pt-3 border-t border-gray-200/60 grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs">
                            <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                              <span className="text-foreground-400 block text-[11px] mb-0.5">付款方式</span>
                              <span className="font-semibold text-foreground-800">
                                {getPaymentMethodText(payment?.payment_method)}
                              </span>
                            </div>
                            <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50">
                              <span className="text-foreground-400 block text-[11px] mb-0.5">实付金额</span>
                              <span className="font-semibold text-emerald-600">
                                {formatMoney(payment?.payment_amount ?? s.apply_amount, payment?.currency || s.currency)}
                              </span>
                            </div>
                            <div className="bg-white/80 p-2 rounded-lg border border-gray-200/50 col-span-2 sm:col-span-2">
                              <span className="text-foreground-400 block text-[11px] mb-0.5">打款单号 / 流水号</span>
                              <span className="font-mono text-foreground-800 text-[11px]">
                                {payment?.payment_no || payment?.bank_transaction_no || '-'}
                                {payment?.remark ? ` (${payment.remark})` : ''}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Daily Revenue Periods Tab Content */}
          {activeTab === 'daily' && <DailyRevenueTab />}

          {/* Projects Tab Content */}
          {activeTab === 'projects' && (
            <div className="space-y-4">
              {projectsRevenueList.length === 0 ? (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                    <i className="ri-funds-box-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-sm text-foreground-500">暂无关联项目收益记录</p>
                </div>
              ) : (
                projectsRevenueList.map((project) => (
                  <Link
                    key={project.projectId}
                    to={`/projects/${project.projectId}`}
                    className="glass-card rounded-xl p-5 hover:border-primary-200/50 transition-all cursor-pointer block"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-sm font-semibold text-foreground-950">{project.projectTitle}</h3>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${project.status === 'active' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                            }`}>
                            {project.status === 'active' ? '进行中' : '已完成'}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-400 mb-3">
                          <span>{project.episodes} 集</span>
                          <span>上次结算 {project.lastSettlement}</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {project.channels.map((ch) => (
                            <span key={ch} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-foreground-500">
                              {ch}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="flex items-center gap-6 flex-shrink-0">
                        {project.pendingConfirmation !== '¥ 0' && (
                          <div className="text-right">
                            <p className="text-sm font-semibold text-accent-600">{project.pendingConfirmation}</p>
                            <p className="text-xs text-foreground-400">待确认</p>
                          </div>
                        )}
                        <div className="text-right">
                          <p className="text-lg font-bold text-foreground-950">{project.totalRevenue}</p>
                          <p className="text-xs text-foreground-400">累计收益</p>
                        </div>
                        <i className="ri-arrow-right-s-line text-foreground-300"></i>
                      </div>
                    </div>
                  </Link>
                ))
              )}
            </div>
          )}
        </>
      )}

      {/* Info footer */}
      <div className="mt-8 p-4 rounded-xl bg-gray-50 border border-gray-200/60 flex items-start gap-3">
        <i className="ri-information-line text-foreground-400 mt-0.5"></i>
        <div className="text-xs text-foreground-500">
          <p className="font-medium text-foreground-700 mb-1">收益结算说明</p>
          <p>每月 1 日自动生成上月收益结算单，请在 5 个工作日内核对确认。确认后，收益将在当月 15 日统一结算打款至您的收款账户。如对结算金额有异议，可在详情中提交异议申请，平台将在 3 个工作日内联系您核实。</p>
        </div>
      </div>

      {/* Settlement Detail Modal */}
      <SettlementDetailModal
        settlementId={selectedSettlementId}
        onClose={() => setSelectedSettlementId(null)}
        onRefresh={loadRevenueData}
      />
    </AccountLayout>
  );
}